import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { VisibilityBadge } from './VisibilityBadge';
import { SessionContextMenu } from './SessionContextMenu';
import { ShareSettingsModal } from './ShareSettingsModal';
import { DeleteSessionModal } from './DeleteSessionModal';
import { useAgentSessions } from './AgentSessionContext';
import { useNavigate } from 'react-router-dom';
import { ChannelLogo } from '../../channels/ChannelLogo';
import { CHANNEL_INFO_BY_CHANNEL, CHANNEL_TYPE_ECOMMERCE } from '../../channels/channels';

const SCOPE_CONFIG = {
    accounts: { icon: 'fas fa-bullhorn', labelKey: 'agent_chat.scope_account' },
    bus: { icon: 'fas fa-layer-group', labelKey: 'agent_chat.scope_bu' },
};

export const ChatHeader = ({ clientName, scopeLabel, scopeType, scopeChannel, scopeAccountId, session, debugToggle }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { userId, entryPoint, clientId, archiveSession, unarchiveSession, deleteSession, showToast, renameSession } = useAgentSessions();

    const [menuPos, setMenuPos] = useState(null);
    const [shareModal, setShareModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const renameInputRef = useRef(null);
    const menuBtnRef = useRef(null);

    const isMine = session ? String(session.creator_id) === String(userId) : true;
    const visibility = session ? session.visibility : null;

    useEffect(() => {
        if (isRenaming && renameInputRef.current) {
            renameInputRef.current.focus();
            renameInputRef.current.select();
        }
    }, [isRenaming]);

    const startRename = () => {
        setRenameValue(session ? session.name : '');
        setIsRenaming(true);
    };

    const submitRename = () => {
        const trimmed = renameValue.trim();
        if (trimmed && session && trimmed !== session.name) {
            renameSession(session.session_id, trimmed);
        }
        setIsRenaming(false);
    };

    const handleRenameKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); submitRename(); }
        if (e.key === 'Escape') { setIsRenaming(false); }
    };

    const handleMenuClick = (e) => {
        e.preventDefault();
        if (menuPos) { setMenuPos(null); return; }
        const rect = menuBtnRef.current.getBoundingClientRect();
        setMenuPos({ top: rect.bottom + 4, left: rect.right - 160 });
    };

    const getNewChatPath = () => {
        if (entryPoint === 'client') return `/client/${clientId}/agent`;
        if (entryPoint === 'portal') return `/portal/agent`;
        return '/agent/chat';
    };

    const handleArchive = (sess) => {
        if (sess.is_archived) {
            unarchiveSession(sess.session_id);
            showToast(t('agent_chat.unarchived_toast'));
        } else {
            archiveSession(sess.session_id, true);
            showToast(t('agent_chat.archived_toast'), () => unarchiveSession(sess.session_id));
        }
    };

    const handleDeleteConfirm = () => {
        if (!session) return;
        deleteSession(session.session_id).then((success) => {
            if (success) {
                setDeleteModal(false);
                navigate(getNewChatPath());
            }
        });
    };

    return (
        <div className="agent-chat__header">
            <div className="agent-chat__header-left">
                {isRenaming ? (
                    <input
                        ref={renameInputRef}
                        className="agent-chat__rename-input"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={handleRenameKeyDown}
                        onBlur={submitRename}
                        placeholder={t('agent_chat.rename_placeholder')}
                        maxLength={200}
                    />
                ) : (
                    <span className="agent-chat__title">{t('agent_chat.title')}</span>
                )}
                {clientName && (
                    <span className="agent-chat__client-badge">
                        <i className="fas fa-user"></i> {clientName}
                    </span>
                )}
                {scopeLabel && (() => {
                    const cfg = SCOPE_CONFIG[scopeType] || SCOPE_CONFIG.accounts;
                    const isEcommerce = scopeChannel && CHANNEL_INFO_BY_CHANNEL[scopeChannel]?.channelType === CHANNEL_TYPE_ECOMMERCE;
                    return (
                        <span className="agent-chat__scope-badge" title={t(cfg.labelKey)}>
                            {scopeChannel
                                ? <ChannelLogo channel={scopeChannel} width="14px" height="14px" hspace={0} vspace={0} useOld={false} />
                                : <i className={cfg.icon}></i>
                            }
                            <span className="agent-chat__scope-type">{t(cfg.labelKey)}:</span>
                            {scopeLabel}
                            {scopeAccountId && !isEcommerce && (
                                <span className="agent-chat__scope-external-id">{scopeAccountId}</span>
                            )}
                        </span>
                    );
                })()}
                {session && (
                    <VisibilityBadge
                        visibility={visibility}
                        onClick={isMine ? () => setShareModal(true) : undefined}
                    />
                )}
            </div>
            <div className="agent-chat__header-right">
                {debugToggle}
                {session && (
                    <button
                        ref={menuBtnRef}
                        className="agent-chat__menu-btn"
                        onClick={handleMenuClick}
                    >
                        <i className="fas fa-ellipsis-v"></i>
                    </button>
                )}
                    {menuPos && (
                    <SessionContextMenu
                        session={session}
                        position={menuPos}
                        isMine={isMine}
                        onRename={() => {
                            setMenuPos(null);
                            startRename();
                        }}
                        onShare={() => setShareModal(true)}
                        onArchive={() => handleArchive(session)}
                        onDelete={() => setDeleteModal(true)}
                        onClose={() => setMenuPos(null)}
                    />
                )}
            </div>

            {shareModal && session && (
                <ShareSettingsModal
                    session={session}
                    onClose={() => setShareModal(false)}
                />
            )}

            {deleteModal && session && (
                <DeleteSessionModal
                    session={session}
                    onConfirm={handleDeleteConfirm}
                    onClose={() => setDeleteModal(false)}
                />
            )}
        </div>
    );
};
